import type {ClientEnvelope, ErrorCode, ServerEnvelope, ServerPayload} from '../../../../packages/protocol/generated/ts/messages';
import type {MessageChunk, ResponseConfig} from './types';
import {uuidv4} from "./uuid";

export type Listener<T> = (data: T) => void;

export interface ChatError {
    code: ErrorCode;
    message: string;
}

export interface RequestHandle {
    requestId: string;
    onText(cb: Listener<string>): () => void;
    onComplete(cb: Listener<void>): () => void;
    cancel(): void;
}

export interface ClientEvents {
    onOpen?: () => void;
    onClose?: (ev: CloseEvent) => void;
    onSocketError?: (ev: Event) => void;
    onErrorMessage?: (err: ChatError) => void;
}

export interface ClientOptions extends ClientEvents {
    gatewayUrl: string; // ws:// or wss://, the session endpoint lives on the same host
    avatarKey: string;
}

type VoicePayload = Extract<ServerPayload, {kind: 'voice'}>;

interface PendingReply {
    onText: Set<Listener<string>>;
    onComplete: Set<Listener<void>>;
    // The gateway sends each chunk's voice first and its blendshapes right after; hold the voice until they arrive.
    heldVoice: VoicePayload | null;
    sequence: number;
}

interface PendingStep {
    expectedKind: 'authenticated' | 'session_started';
    resolve: () => void;
    reject: (err: Error) => void;
}

export class AvatarChatClient {

    private ws: WebSocket | null = null;
    private options: ClientOptions;
    private steps = new Map<string, PendingStep>();
    private replies = new Map<string, PendingReply>();
    private sessionId: string | null = null;
    private messageQueue: MessageChunk[] = [];

    messageQueueUpdated: () => void = () => {};
    getNextMessageChunk(): MessageChunk | null {
        return this.messageQueue.shift() || null;
    }

    constructor(options: ClientOptions) {
        this.options = options;
    }

    isConnected(): boolean {
        return this.ws?.readyState === WebSocket.OPEN;
    }

    getSessionId(): string | null {
        return this.sessionId;
    }

    /** Requests a one-time session token, opens the socket, authenticates and starts a session. */
    async connect(): Promise<void> {
        if (this.ws && (this.ws.readyState === WebSocket.OPEN || this.ws.readyState === WebSocket.CONNECTING)) {
            return;
        }

        const sessionToken = await this.requestSessionToken();
        await this.openSocket();
        await this.sendStep('authenticated', {action: 'authenticate', params: {session_token: sessionToken}});
        await this.sendStep('session_started', {action: 'start_session', params: {}});
    }

    private async requestSessionToken(): Promise<string> {
        const url = new URL('/v1/session', this.options.gatewayUrl);
        url.protocol = url.protocol === 'wss:' ? 'https:' : 'http:';

        const res = await fetch(url, {
            method: 'POST',
            headers: {'Content-Type': 'application/json'},
            body: JSON.stringify({avatar_key: this.options.avatarKey}),
        });
        const body = await res.json();
        if (!res.ok || typeof body.session_token !== 'string') {
            throw new Error(body.error || `Session request failed (${res.status})`);
        }
        return body.session_token;
    }

    private openSocket(): Promise<void> {
        return new Promise((resolve, reject) => {
            const ws = new WebSocket(this.options.gatewayUrl);
            this.ws = ws;
            ws.onopen = () => {
                this.options.onOpen?.();
                resolve();
            };
            ws.onerror = (ev) => {
                this.options.onSocketError?.(ev);
                if (ws.readyState !== WebSocket.OPEN) reject(new Error('WebSocket connection error'));
            };
            ws.onclose = (ev) => {
                this.sessionId = null;
                this.steps.forEach((step) => step.reject(new Error('WebSocket closed')));
                this.steps.clear();
                this.replies.clear();
                this.options.onClose?.(ev);
            };
            ws.onmessage = (ev) => this.handleMessage(ev.data);
        });
    }

    private sendStep(expectedKind: PendingStep['expectedKind'], payload: ClientEnvelope['payload']): Promise<void> {
        const request_id = uuidv4();
        return new Promise((resolve, reject) => {
            this.steps.set(request_id, {expectedKind, resolve, reject});
            try {
                this.send({type: 'request', request_id, payload});
            } catch (e) {
                this.steps.delete(request_id);
                reject(e);
            }
        });
    }

    disconnect(code?: number, reason?: string): void {
        if (this.ws) {
            try {
                this.ws.close(code, reason);
            } catch (e) {
                console.error('[chat] closing the socket failed', e);
            }
        }
        this.ws = null;
        this.sessionId = null;
        this.steps.clear();
        this.replies.clear();
    }

    private send(obj: ClientEnvelope) {
        if (!this.ws || this.ws.readyState !== WebSocket.OPEN) throw new Error('WebSocket is not open');
        this.ws.send(JSON.stringify(obj));
    }

    private handleMessage(data: any) {
        let msg: ServerEnvelope;
        try {
            msg = JSON.parse(data);
        } catch (e) {
            console.warn('Invalid JSON message:', data);
            return;
        }

        const requestId = msg.request_id;
        const payload = msg.payload;

        const step = this.steps.get(requestId);
        if (step) {
            this.steps.delete(requestId);
            if (payload.kind === 'error') {
                step.reject(new Error(`${payload.code}: ${payload.message}`));
                this.options.onErrorMessage?.({code: payload.code, message: payload.message});
            } else if (payload.kind === step.expectedKind) {
                if (payload.kind === 'session_started') this.sessionId = payload.session_id;
                step.resolve();
            } else {
                step.reject(new Error(`Expected ${step.expectedKind}, got ${payload.kind}`));
            }
            return;
        }

        if (payload.kind === 'error') {
            this.replies.delete(requestId);
            this.options.onErrorMessage?.({code: payload.code, message: payload.message});
            return;
        }

        const reply = this.replies.get(requestId);
        if (!reply) {
            console.error('[chat] message for no open request; dropped', msg);
            return;
        }

        switch (payload.kind) {
            case 'text':
                reply.onText.forEach((cb) => cb(payload.delta));
                return;
            case 'voice':
                if (reply.heldVoice) {
                    console.error('[chat] two voice messages without blendshapes between them; playing the first without animation', msg);
                    this.flushHeldVoice(requestId, reply);
                }
                reply.heldVoice = payload;
                return;
            case 'blendshapes':
                if (!reply.heldVoice) {
                    console.error('[chat] blendshapes without a preceding voice message; playing them without audio', msg);
                }
                this.emitChunk(requestId, reply, reply.heldVoice, payload.frames, payload.frame_rate);
                reply.heldVoice = null;
                return;
            case 'response_complete':
                this.flushHeldVoice(requestId, reply);
                this.replies.delete(requestId);
                reply.onComplete.forEach((cb) => cb());
                return;
            case 'authenticated':
            case 'session_started':
                console.error('[chat] handshake reply for a text request; dropped', msg);
                return;
            default: {
                const unknown: never = payload;
                console.error('[chat] unknown message kind; dropped', unknown);
            }
        }
    }

    /** A voice with no blendshapes behind it, for example when include_blendshapes is off. */
    private flushHeldVoice(requestId: string, reply: PendingReply) {
        if (reply.heldVoice) {
            this.emitChunk(requestId, reply, reply.heldVoice, [], 0);
            reply.heldVoice = null;
        }
    }

    private emitChunk(requestId: string, reply: PendingReply, voice: VoicePayload | null, frames: MessageChunk['blendshapes'], frameRate: number) {
        // atob length is the byte count; 16-bit mono gives two bytes per sample.
        const sampleCount = voice ? atob(voice.audio_data).length / (voice.bit_depth / 8) : 0;
        reply.sequence++;
        this.messageQueue.push({
            request_id: requestId,
            sequence: reply.sequence,
            voice_data: {
                format: 'pcm',
                sample_rate: voice?.sample_rate ?? 0,
                channels: 1,
                bit_depth: voice?.bit_depth ?? 16,
                duration_ms: voice ? (sampleCount / voice.sample_rate) * 1000 : 0,
                data: voice?.audio_data ?? '',
            },
            blendshapes: frames,
            frame_rate: frameRate,
        });
        this.messageQueueUpdated();
    }

    sendText(text: string, response?: ResponseConfig): RequestHandle {
        if (!this.sessionId) throw new Error('No active session');
        const request_id = uuidv4();
        const reply: PendingReply = {onText: new Set(), onComplete: new Set(), heldVoice: null, sequence: 0};
        this.replies.set(request_id, reply);
        try {
            this.send({type: 'request', request_id, payload: {action: 'send_text', params: {text}, response_config: response}});
        } catch (e) {
            this.replies.delete(request_id);
            throw e;
        }

        return {
            requestId: request_id,
            onText: (cb) => {
                reply.onText.add(cb);
                return () => reply.onText.delete(cb);
            },
            onComplete: (cb) => {
                reply.onComplete.add(cb);
                return () => reply.onComplete.delete(cb);
            },
            cancel: () => {
                try {
                    this.send({type: 'request', request_id: uuidv4(), payload: {action: 'stop_command', params: {target_request_id: request_id}}});
                } catch (e) {
                    console.error('[chat] stop_command not sent', e);
                }
            },
        };
    }
}
